import { environment } from 'src/environments/environment';

const VERSION_STORAGE_KEY = 'fixz_app_version';
const VERSION_QUERY_PARAM = '_v';

/**
 * Fetches /assets/version.json with cache bypass and hard-refreshes
 * when the deployed version is newer than the running bundle.
 *
 * Bump `environment.appVersion` and `src/assets/version.json` together
 * on each release so returning users drop stale cached JS/CSS.
 */
export async function ensureLatestVersion(): Promise<void> {
  if (typeof window === 'undefined') {
    return;
  }

  try {
    const response = await fetch(`/assets/version.json?t=${Date.now()}`, {
      cache: 'no-store',
      headers: {
        'Cache-Control': 'no-cache',
        Pragma: 'no-cache',
      },
    });
    if (!response.ok) {
      return;
    }

    const data = await response.json();
    const remoteVersion = String(data?.version ?? '').trim();
    const runningVersion = String(environment.appVersion ?? '').trim();
    if (!remoteVersion) {
      return;
    }

    try {
      localStorage.setItem(VERSION_STORAGE_KEY, remoteVersion);
    } catch {
      // ignore storage failures
    }

    if (remoteVersion === runningVersion) {
      clearVersionQueryParam();
      return;
    }

    const url = new URL(window.location.href);
    if (url.searchParams.get(VERSION_QUERY_PARAM) === remoteVersion) {
      return;
    }

    await clearBrowserCaches();
    url.searchParams.set(VERSION_QUERY_PARAM, remoteVersion);
    window.location.replace(url.toString());
    await new Promise<void>(() => {
      // Wait for the navigation so Angular does not boot the stale bundle.
    });
  } catch {
    // Boot normally if the version file cannot be reached.
  }
}

function clearVersionQueryParam(): void {
  const url = new URL(window.location.href);
  if (!url.searchParams.has(VERSION_QUERY_PARAM)) {
    return;
  }
  url.searchParams.delete(VERSION_QUERY_PARAM);
  window.history.replaceState({}, '', url.toString());
}

async function clearBrowserCaches(): Promise<void> {
  try {
    if ('serviceWorker' in navigator) {
      const registrations = await navigator.serviceWorker.getRegistrations();
      await Promise.all(registrations.map((registration) => registration.unregister()));
    }
  } catch {
    // ignore
  }

  try {
    if ('caches' in window) {
      const keys = await caches.keys();
      await Promise.all(keys.map((key) => caches.delete(key)));
    }
  } catch {
    // ignore
  }
}
